import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { currentPushState, enablePush } from "../push";
import type { PushState } from "../pushSupport";
import type { FigmaBridgeToken, Firing, FiringStatus, ProfileSummary, Repeat, ScheduleDetail, ScheduleList, SchedulePreset, ScheduleSummary } from "../types";
import { composeText, decomposeText, describeRepeat, needsDeviceWarning, type ScheduleMessage } from "./scheduleText";
import { RepeatField, when } from "../components/RepeatField";
import { BellIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, FigmaIcon, MenuIcon, PlayIcon, PlusIcon } from "../icons";
import { Avatar } from "../avatar";
import { useIsNarrow } from "../viewport";
import { usePhoneDetail } from "./phonePage";
import { took } from "./loopGlance";

const POLL_MS = 5000;

const FIRING_LABEL: Record<FiringStatus, string> = {
	running: "运行中",
	succeeded: "成功",
	failed: "失败",
	skipped: "已跳过",
	interrupted: "已中断",
};

const PUSH_TEXT: Record<PushState, string> = {
	unsupported: "此浏览器不支持推送通知",
	"needs-home-screen": "请先用 Safari 将此页面添加到主屏幕，再从主屏幕打开",
	denied: "通知权限已拒绝，请在系统设置中开启",
	off: "此设备未开启通知",
	on: "此设备已开启通知",
};

function errorText(err: unknown, fallback: string): string {
	return err instanceof ApiError ? err.message : fallback;
}

export function Schedules(props: { profiles: ProfileSummary[]; openId: string | null; onMenu: () => void; onOpenSession: (id: string) => void }) {
	const [list, setList] = useState<ScheduleList | null>(null);
	const [currentId, setCurrentId] = useState<string | null>(props.openId);
	const [detail, setDetail] = useState<ScheduleDetail | null>(null);
	const [editing, setEditing] = useState<ScheduleSummary | "new" | null>(null);
	const [error, setError] = useState<string | null>(null);
	const phone = usePhoneDetail(useIsNarrow(), "schedulePage", () => setCurrentId(null));

	useEffect(() => {
		if (props.openId) setCurrentId(props.openId);
	}, [props.openId]);

	function openSchedule(id: string) {
		setCurrentId(id);
		phone.opened();
	}

	const refresh = useCallback(async () => {
		try {
			setList(await api.schedules());
		} catch (err) {
			setError(errorText(err, "加载定时任务失败"));
		}
	}, []);

	const refreshDetail = useCallback(async (id: string) => {
		try {
			setDetail(await api.schedule(id));
		} catch (err) {
			if (err instanceof ApiError && (err.status === 404 || err.status === 403)) setCurrentId(null);
		}
	}, []);

	// Firings are minutes apart, so polling beats a second event stream.
	useEffect(() => {
		void refresh();
		const timer = setInterval(() => void refresh(), POLL_MS);
		return () => clearInterval(timer);
	}, [refresh]);

	useEffect(() => {
		setDetail(null);
		if (!currentId) return;
		void refreshDetail(currentId);
		const timer = setInterval(() => void refreshDetail(currentId), POLL_MS);
		return () => clearInterval(timer);
	}, [currentId, refreshDetail]);

	async function act(fn: () => Promise<unknown>, failure: string) {
		try {
			await fn();
			await refresh();
			if (currentId) await refreshDetail(currentId);
		} catch (err) {
			setError(errorText(err, failure));
		}
	}

	async function enable(preset: SchedulePreset) {
		await act(async () => {
			const { schedule } = await api.createSchedule({ preset: { profile: preset.profile, name: preset.name } });
			openSchedule(schedule.id);
		}, "启用失败");
	}

	async function remove(schedule: ScheduleSummary) {
		if (!confirm(`删除「${schedule.title}」？已创建的对话将保留。`)) return;
		await act(async () => {
			await api.deleteSchedule(schedule.id);
			phone.back();
		}, "删除失败");
	}

	return (
		<main className={`chat schedules split-page${currentId ? " detail-open" : ""}`}>
			{error && (
				<p className="banner error">
					{error}
					<button className="banner-close" title="关闭" onClick={() => setError(null)}>
						×
					</button>
				</p>
			)}
			<div className="split">
				<section className="split-list">
					<header className="chat-head page-head">
						<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={props.onMenu}>
							<MenuIcon size={22} />
						</button>
						<div className="titles">
							<span className="title">定时任务</span>
						</div>
						<button className="new-btn" aria-label="新建" title="新建" onClick={() => setEditing("new")}>
							<PlusIcon size={22} />
						</button>
					</header>
					<div className="split-scroll">
						<div className="row-group">
							<PushBar warn={list ? needsDeviceWarning(list.schedules, list.pushDevices) : false} onDevicesChanged={() => void refresh()} />
							<FigmaBridgeBar />
						</div>
						{list && list.schedules.length === 0 && <p className="hint list-hint">暂无定时任务</p>}
						{list && list.schedules.length > 0 && (
							<div className="row-group">
								{list.schedules.map((s) => (
									<button key={s.id} className={`list-row with-avatar${s.id === currentId ? " current" : ""}`} onClick={() => openSchedule(s.id)}>
										<Avatar name={s.profile} size={42} />
										<span className="list-row-text">
											<span className="list-row-head">
												<span className={`list-row-title${s.paused ? " dim" : ""}`}>{s.title}</span>
												{s.paused ? <span className="pill pill-paused">已暂停</span> : s.lastFiring ? <FiringPill status={s.lastFiring.status} /> : null}
											</span>
											<span className="list-row-sub">
												{s.profile} · {describeRepeat(s.repeat)}
												{s.paused ? "" : ` · 下次 ${when(s.nextRunAt)}`}
											</span>
										</span>
									</button>
								))}
							</div>
						)}
						{list && list.presets.length > 0 && (
							<>
								<h3 className="list-label">可启用的预设</h3>
								<div className="row-group">
									{list.presets.map((p) => (
										<div key={`${p.profile}/${p.name}`} className="list-row with-avatar static">
											<Avatar name={p.profile} size={42} />
											<span className="list-row-text">
												<span className="list-row-title">{p.title}</span>
												<span className="list-row-sub">
													{p.profile} · {describeRepeat(p.repeat)}
												</span>
											</span>
											<button className="soft-btn" onClick={() => void enable(p)}>
												启用
											</button>
										</div>
									))}
								</div>
							</>
						)}
					</div>
				</section>
				<section className="split-detail">
					{currentId &&
						(detail?.schedule.id === currentId ? (
							<ScheduleView
								detail={detail}
								onBack={phone.back}
								onFire={() => void act(() => api.fireSchedule(detail.schedule.id), "启动失败")}
								onTogglePause={() => void act(() => api.updateSchedule(detail.schedule.id, { paused: !detail.schedule.paused }), "操作失败")}
								onEdit={() => setEditing(detail.schedule)}
								onDelete={() => void remove(detail.schedule)}
								onOpenSession={props.onOpenSession}
							/>
						) : (
							<>
								<header className="detail-head">
									<button className="ghost icon-btn detail-back" aria-label="返回" title="返回" onClick={phone.back}>
										<ChevronLeftIcon size={24} />
									</button>
								</header>
								<p className="hint detail-hint">加载中…</p>
							</>
						))}
				</section>
			</div>
			{editing && (
				<ScheduleForm
					profiles={props.profiles}
					schedule={editing === "new" ? null : editing}
					onClose={() => setEditing(null)}
					onSaved={(saved) => {
						setEditing(null);
						openSchedule(saved.id);
						void refresh();
						void refreshDetail(saved.id);
					}}
				/>
			)}
		</main>
	);
}

/** This device's notification state. Turning it on must stay a direct click: iOS only prompts for a user gesture. */
function PushBar({ warn, onDevicesChanged }: { warn: boolean; onDevicesChanged: () => void }) {
	const [state, setState] = useState<PushState | null>(null);
	const [busy, setBusy] = useState(false);
	const [note, setNote] = useState<string | null>(null);

	useEffect(() => {
		void currentPushState()
			.then(setState)
			.catch(() => setState("unsupported"));
	}, []);

	async function turnOn() {
		setBusy(true);
		setNote(null);
		try {
			setState(await enablePush());
			onDevicesChanged();
		} catch (err) {
			setNote(`开启失败：${(err as Error).message}`);
		} finally {
			setBusy(false);
		}
	}

	async function sendTest() {
		setBusy(true);
		try {
			const { devices } = await api.testPush();
			setNote(devices === 0 ? "暂无订阅推送的设备" : `已发送到 ${devices} 台设备`);
		} catch (err) {
			setNote(errorText(err, "发送失败"));
		} finally {
			setBusy(false);
		}
	}

	if (!state) return null;
	return (
		<div className="list-row with-avatar static">
			<span className="glyph-circle tint-blue">
				<BellIcon size={17} />
			</span>
			<span className="list-row-text">
				<span className="list-row-line">{PUSH_TEXT[state]}</span>
				{warn && <span className="list-row-sub tone-warn">暂无订阅推送的设备</span>}
				{note && <span className="list-row-sub wrap">{note}</span>}
			</span>
			{state === "off" && (
				<button className="soft-btn" onClick={() => void turnOn()} disabled={busy}>
					开启通知
				</button>
			)}
			{state === "on" && (
				<button className="soft-btn neutral" onClick={() => void sendTest()} disabled={busy}>
					发送测试
				</button>
			)}
		</div>
	);
}

/** Bridge tokens for the Figma plugin: minted here, shown once, pasted into the plugin. */
function FigmaBridgeBar() {
	const [tokens, setTokens] = useState<FigmaBridgeToken[]>([]);
	const [open, setOpen] = useState(false);
	const [label, setLabel] = useState("");
	const [minted, setMinted] = useState<string | null>(null);
	const [note, setNote] = useState<string | null>(null);

	async function refresh() {
		try {
			setTokens((await api.figmaTokens()).tokens);
		} catch (err) {
			setNote(errorText(err, "加载失败"));
		}
	}

	useEffect(() => {
		if (open) void refresh();
	}, [open]);

	async function mint() {
		if (!label.trim()) return;
		try {
			const { token } = await api.mintFigmaToken(label.trim());
			setMinted(token);
			setLabel("");
			await refresh();
		} catch (err) {
			setNote(errorText(err, "生成失败"));
		}
	}

	async function revoke(id: string) {
		try {
			await api.revokeFigmaToken(id);
			await refresh();
		} catch (err) {
			setNote(errorText(err, "撤销失败"));
		}
	}

	return (
		<>
			<button className="list-row with-avatar" aria-expanded={open} onClick={() => setOpen(!open)}>
				<span className="glyph-circle tint-violet">
					<FigmaIcon size={17} />
				</span>
				<span className="list-row-text">
					<span className="list-row-line">Figma 桥接</span>
				</span>
				<span className="list-row-when">{open && tokens.length > 0 ? `令牌 · ${tokens.length}` : "令牌"}</span>
				{open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
			</button>
			{open && (
				<div className="figma-tokens">
					{tokens.map((t) => (
						<span key={t.id} className="figma-token">
							<span>
								{t.label}（{t.id}）
							</span>
							<button className="ghost" onClick={() => void revoke(t.id)}>
								撤销
							</button>
						</span>
					))}
					<span className="figma-mint">
						<input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="设备名称" aria-label="设备名称" />
						<button onClick={() => void mint()} disabled={!label.trim()}>
							生成令牌
						</button>
					</span>
					{minted && (
						<span className="hint">
							仅显示一次，请复制到 Figma 插件：<code>{minted}</code>
						</span>
					)}
					{note && <span className="hint">{note}</span>}
				</div>
			)}
		</>
	);
}

function FiringPill({ status }: { status: FiringStatus }) {
	return <span className={`pill pill-${status}`}>{FIRING_LABEL[status]}</span>;
}

function ScheduleView(props: {
	detail: ScheduleDetail;
	onBack: () => void;
	onFire: () => void;
	onTogglePause: () => void;
	onEdit: () => void;
	onDelete: () => void;
	onOpenSession: (id: string) => void;
}) {
	const { schedule, firings } = props.detail;
	const running = firings.some((f) => f.status === "running");
	// Beside the title on a wide screen, under it on a phone.
	const actions = (where: string) => (
		<span className={`schedule-actions ${where}`}>
			<button className="head-btn" onClick={props.onTogglePause}>
				{schedule.paused ? "恢复" : "暂停"}
			</button>
			<button className="head-btn" onClick={props.onEdit}>
				编辑
			</button>
			<button className="head-btn danger" onClick={props.onDelete} disabled={running}>
				删除
			</button>
			<button className="head-btn primary" onClick={props.onFire} disabled={running}>
				<PlayIcon size={13} />
				{running ? "运行中…" : "立即运行"}
			</button>
		</span>
	);
	return (
		<>
			<header className="detail-head">
				<button className="ghost icon-btn detail-back" aria-label="返回" title="返回" onClick={props.onBack}>
					<ChevronLeftIcon size={24} />
				</button>
				<Avatar name={schedule.profile} size={40} />
				<span className="detail-titles">
					<span className="detail-title">{schedule.title}</span>
					<span className="detail-sub">
						{schedule.profile} · {describeRepeat(schedule.repeat)} · {schedule.paused ? "已暂停" : `下次 ${when(schedule.nextRunAt)}`}
					</span>
				</span>
				{actions("in-head")}
			</header>
			<div className="detail-body wall">
				<div className="schedule-view">
					{actions("in-body")}
					<h3 className="section-label">发送的消息</h3>
					<div className="sent">
						<div className="sent-bubble">{schedule.text}</div>
						<span className="sent-meta">{schedule.notify ? "推送全部结果" : "仅推送失败"}</span>
					</div>
					<h3 className="section-label">运行记录</h3>
					{firings.length === 0 ? (
						<p className="hint">暂无运行记录</p>
					) : (
						<div className="firings">
							{firings.map((f) => (
								<FiringView key={f.id} firing={f} onOpenSession={props.onOpenSession} />
							))}
						</div>
					)}
				</div>
			</div>
		</>
	);
}

function FiringView({ firing, onOpenSession }: { firing: Firing; onOpenSession: (id: string) => void }) {
	const [full, setFull] = useState(false);
	const duration = firing.finishedAt && firing.status !== "skipped" ? Math.round((firing.finishedAt - firing.startedAt) / 1000) : null;
	return (
		<div className="firing">
			<div className="firing-head">
				<FiringPill status={firing.status} />
				{firing.manual && <span className="pill">手动</span>}
				<span className="firing-when">{when(firing.scheduledFor ?? firing.startedAt)}</span>
				{duration !== null && <span className="firing-took">{took(duration)}</span>}
				{firing.sessionId && (
					<button className="link-btn" onClick={() => onOpenSession(firing.sessionId!)}>
						查看对话 ›
					</button>
				)}
			</div>
			{firing.status === "skipped" && <p className="firing-note">上次运行未结束，已跳过</p>}
			{firing.error && <p className="firing-box tone-err">{firing.error}</p>}
			{firing.warnings.map((w, i) => (
				<p key={i} className="firing-box tone-warn">
					{w}
				</p>
			))}
			{firing.output && (
				<button className={`firing-output${full ? " full" : ""}`} aria-expanded={full} onClick={() => setFull(!full)}>
					{firing.output}
				</button>
			)}
		</div>
	);
}

function ScheduleForm(props: { profiles: ProfileSummary[]; schedule: ScheduleSummary | null; onClose: () => void; onSaved: (schedule: ScheduleSummary) => void }) {
	const { schedule } = props;
	const workflowsOf = (name: string) => props.profiles.find((p) => p.name === name)?.workflows ?? [];
	const firstMessage = (name: string): ScheduleMessage => {
		const first = workflowsOf(name)[0];
		return first ? { kind: "workflow", workflow: first.name, args: "" } : { kind: "prompt", prompt: "" };
	};

	const [profile, setProfile] = useState(schedule?.profile ?? props.profiles[0]?.name ?? "");
	const [message, setMessage] = useState<ScheduleMessage>(() =>
		schedule ? decomposeText(schedule.text, workflowsOf(schedule.profile).map((w) => w.name)) : firstMessage(profile),
	);
	const [title, setTitle] = useState(schedule?.title ?? "");
	const [repeat, setRepeat] = useState<Repeat>(schedule?.repeat ?? { every: "day", at: "08:00" });
	const [notify, setNotify] = useState(schedule?.notify ?? true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const workflows = workflowsOf(profile);
	const hint = message.kind === "workflow" ? workflows.find((w) => w.name === message.workflow)?.argumentHint : undefined;

	function pickProfile(name: string) {
		setProfile(name);
		setMessage(firstMessage(name));
	}

	async function submit() {
		const text = composeText(message);
		if (!title.trim()) return setError("标题不能为空");
		if (!text.trim()) return setError("消息不能为空");
		setBusy(true);
		setError(null);
		try {
			const saved = schedule
				? (await api.updateSchedule(schedule.id, { title: title.trim(), repeat, text, notify })).schedule
				: (await api.createSchedule({ profile, title: title.trim(), repeat, text, notify })).schedule;
			props.onSaved(saved);
		} catch (err) {
			setError(errorText(err, "保存失败"));
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="modal" onClick={props.onClose}>
			<div className="modal-card loop-form" onClick={(e) => e.stopPropagation()}>
				<header>
					<h3>{schedule ? "编辑定时任务" : "新建定时任务"}</h3>
					<button className="ghost" onClick={props.onClose}>
						关闭
					</button>
				</header>
				<div className="form">
					<label>
						标题
						<input value={title} onChange={(e) => setTitle(e.target.value)} />
					</label>
					<label>
						Profile
						<select value={profile} disabled={schedule !== null} onChange={(e) => pickProfile(e.target.value)}>
							{props.profiles.map((p) => (
								<option key={p.name} value={p.name}>
									{p.name} — {p.description}
								</option>
							))}
						</select>
					</label>
					{workflows.length > 0 && (
						<div className="row">
							<label className="inline">
								<input
									type="radio"
									checked={message.kind === "workflow"}
									onChange={() => setMessage({ kind: "workflow", workflow: workflows[0]!.name, args: "" })}
								/>
								执行 workflow
							</label>
							<label className="inline">
								<input type="radio" checked={message.kind === "prompt"} onChange={() => setMessage({ kind: "prompt", prompt: "" })} />
								自定义消息
							</label>
						</div>
					)}
					{message.kind === "workflow" ? (
						<>
							<label>
								Workflow
								<select value={message.workflow} onChange={(e) => setMessage({ ...message, workflow: e.target.value })}>
									{workflows.map((w) => (
										<option key={w.name} value={w.name}>
											/{w.name} — {w.description}
										</option>
									))}
								</select>
							</label>
							<label>
								参数
								<input value={message.args} placeholder={hint} onChange={(e) => setMessage({ ...message, args: e.target.value })} />
							</label>
						</>
					) : (
						<label>
							消息
							<textarea value={message.prompt} rows={5} onChange={(e) => setMessage({ kind: "prompt", prompt: e.target.value })} />
						</label>
					)}
					<RepeatField repeat={repeat} onChange={setRepeat} />
					{schedule && !schedule.repeat && <span className="hint">旧 cron 规则无法显示，保存后将被替换</span>}
					<label className="inline">
						<input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
						推送成功结果
					</label>
					{error && <p className="banner error">{error}</p>}
					<div className="row">
						<button onClick={() => void submit()} disabled={busy}>
							{busy ? "保存中…" : "保存"}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
